import type { DatasetId } from "../server/servicem8/datasets";
import { hasScope } from "./scopes";

/**
 * Metadata for a tool. Shared by server (to decide access) and web (to render the launcher).
 * Each tool lives in src/tools/<id>/ with:
 *   meta.ts    – this object
 *   server.ts  – optional Hono router mounted at /api/c/:companyId/tools/<id>
 *   web.tsx    – default-exported React component
 */
export interface ToolMeta {
    id: string;
    name: string;
    description: string;
    /** lucide-react icon name, e.g. "ClipboardList". */
    icon: string;
    /** ServiceM8 OAuth scopes the company connection must have granted. */
    scopes?: string[];
    /**
     * Synced datasets (server/servicem8/datasets.ts ids) its Home alerts and search read, so Home
     * refetches when they change. Its own data is refetched on an `invalidate` for [<id>].
     */
    datasets?: readonly DatasetId[];
    /**
     * Pages within the tool, shown under it in the sidebar (tabs on phones) at /t/<id>/<section>.
     * The first is the default.
     */
    sections?: ToolSection[];
    /** Still being tried out: a "Beta" tag shows by its name and on all its pages. */
    beta?: boolean;
    /**
     * What each ServiceM8 security role can do in it, set per role in Settings › Access (like
     * ServiceM8's own security roles): grouped, each a dropdown. The first is always "use" (open it at all).
     */
    permissions?: readonly ToolPermission[];
}

/** One option of a permission's dropdown. */
export interface PermissionOption {
    value: string;
    label: string;
}

/** Enabled or Disabled: what most permissions offer. */
export const ENABLED_DISABLED: readonly PermissionOption[] = [
    { value: "enabled", label: "Enabled" },
    { value: "disabled", label: "Disabled" },
];

/**
 * One thing a security role can or can't do in a tool. Its options run from the most it allows to
 * the least (Enabled/Disabled unless given); `viewOnly` is its value for a role that can only look
 * (the least, unless given): a role's settings before anyone chose start from its old access level.
 */
export interface ToolPermission {
    id: string;
    /** The heading it's under (e.g. "Items"). */
    group: string;
    label: string;
    /** What it allows, in a line. */
    description: string;
    options?: readonly PermissionOption[];
    viewOnly?: string;
    /** Another of the tool's permissions this one needs (e.g. Manage pricing needs See costs): without it, this allows nothing, whatever it's set to. */
    requires?: string;
    /**
     * Until a manager sets it, on for a role with any of these on (e.g. See costs, added later, for
     * roles that already manage pricing), so adding it takes nothing away. Else from the role's level.
     */
    startsOnWith?: readonly string[];
}

/** Every tool's first permission: open it at all. */
export const USE_PERMISSION = "use";

export const optionsOf = (p: ToolPermission) => p.options ?? ENABLED_DISABLED;

/** A role's value for each of a tool's permissions (permission id → option value). */
export type PermissionValues = Record<string, string>;

export interface ToolSection {
    id: string;
    name: string;
    /** lucide-react icon name, like ToolMeta.icon. */
    icon: string;
    /** Still being tried out (when the tool as a whole isn't): a "Beta" tag shows by its name. */
    beta?: boolean;
}

/** Whether the ServiceM8 connection allows the tool (who may use it is tool access, by security role). */
export function canUseTool(tool: ToolMeta, grantedScopes: string[]): boolean {
    return (tool.scopes ?? []).every((s) => hasScope(grantedScopes, s));
}

/** A tool's permissions: its own, or just "use" for a tool that has none. */
export const permissionsOf = (tool: Pick<ToolMeta, "name" | "permissions">): readonly ToolPermission[] =>
    tool.permissions ?? [{ id: USE_PERMISSION, group: "High level", label: `Use the ${tool.name}`, description: "Open it." }];

/**
 * Whether permission `id` is set to at least option `atLeast` (options run most to least), by
 * default its first (the most: "Enabled"), whatever it `requires`. An unknown permission, or one
 * with no value, isn't.
 */
export function setTo(tool: Pick<ToolMeta, "name" | "permissions">, values: PermissionValues, id: string, atLeast?: string): boolean {
    const permission = permissionsOf(tool).find((p) => p.id === id);
    if (!permission) return false;
    const options = optionsOf(permission).map((o) => o.value);
    const have = options.indexOf(values[id] ?? "");
    return have >= 0 && have <= options.indexOf(atLeast ?? options[0] ?? "");
}

/** Whether `values` allow permission `id` (at least `atLeast`): it's set to that (setTo), and so is any permission it `requires`. */
export function allows(tool: Pick<ToolMeta, "name" | "permissions">, values: PermissionValues, id: string, atLeast?: string): boolean {
    if (!setTo(tool, values, id, atLeast)) return false;
    const needs = permissionsOf(tool).find((p) => p.id === id)?.requires;
    return !needs || allows(tool, values, needs);
}

/** The access levels tools had before permissions: where a role's permissions start (see valuesForLevel). */
export type LegacyLevel = "edit" | "add" | "view" | "none";

/**
 * A role's permissions from its old access level: Can edit allows everything; View only (and Add
 * only) just use, and each permission's view-only value (the least, unless it says); No access nothing.
 */
export function valuesForLevel(tool: Pick<ToolMeta, "name" | "permissions">, level: LegacyLevel): PermissionValues {
    return Object.fromEntries(
        permissionsOf(tool).map((p) => {
            const options = optionsOf(p).map((o) => o.value);
            const most = options[0] ?? "";
            const least = options.at(-1) ?? "";
            if (level === "edit") return [p.id, most];
            if (p.id === USE_PERMISSION) return [p.id, level === "none" ? least : most];
            return [p.id, p.viewOnly ?? least];
        }),
    );
}
